import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useLocalePath, useT } from "@/i18n/LocaleContext";

const STORAGE_KEY = "qcm_cookie_dismissed";

const readInitialVisible = () => {
  if (typeof window === "undefined") return false;
  try {
    return !window.localStorage.getItem(STORAGE_KEY);
  } catch (_e) {
    return false;
  }
};

/* CookieBanner — visibility is React state, motion is CSS transition.
   The entrance uses a class toggle scheduled via requestAnimationFrame
   so that:
     • The banner IS in the DOM on the first commit when visible === true.
     • A single paint later, `in` is added, triggering a plain opacity /
       transform transition to the visible state.
     • Under prefers-reduced-motion, the global override collapses the
       transition to ~0ms — the banner still ends fully visible because
       the terminal state is opacity 1 / translate-y 0.
   Nothing depends on a keyframe animation completing.
   Dismiss reverses the same transition, then unmounts the node. */
export const CookieBanner = () => {
  const { t } = useT();
  const localePath = useLocalePath();

  const [visible, setVisible] = useState(false);
  const [inClass, setInClass] = useState(false);
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    if (!readInitialVisible()) return;
    setVisible(true);
    // Two rAFs: first commits the element mounted at initial state (opacity 0),
    // second flips the class so the transition runs from 0 → 1.
    let r2;
    const r1 = requestAnimationFrame(() => {
      r2 = requestAnimationFrame(() => setInClass(true));
    });
    return () => {
      cancelAnimationFrame(r1);
      if (r2) cancelAnimationFrame(r2);
    };
  }, []);

  const dismiss = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    } catch (_e) {}
    setLeaving(true);
    setInClass(false);
    window.setTimeout(() => setVisible(false), 500);
  };

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie notice"
      data-testid="cookie-banner"
      className={[
        "fixed inset-x-0 bottom-0 z-50 bg-navy text-cream border-t border-gold/60",
        "transition-[opacity,transform] duration-[600ms] ease-[cubic-bezier(0.16,1,0.3,1)]",
        "will-change-[opacity,transform]",
        inClass && !leaving ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
        leaving ? "pointer-events-none" : "",
      ].join(" ")}
    >
      <div className="mx-auto max-w-[1360px] px-6 md:px-10 lg:px-14 py-5 md:py-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-8">
        <p className="font-sans text-[13.5px] font-normal leading-relaxed text-cream/85 flex-1">
          {t("cookie.copy_prefix")}
          <Link
            to={localePath("/legal#cookies")}
            data-testid="cookie-banner-policy-link"
            className="underline decoration-gold/70 underline-offset-4 text-cream hover:text-cream"
          >
            {t("cookie.policy_link")}
          </Link>
          {t("cookie.copy_suffix")}
        </p>
        <button
          type="button"
          data-testid="cookie-banner-dismiss"
          onClick={dismiss}
          className="font-sans text-[11px] font-medium uppercase text-cream border border-cream/40 hover:border-cream px-6 py-3 transition-colors duration-500 self-start md:self-auto"
          style={{ letterSpacing: "0.24em" }}
        >
          {t("cookie.dismiss")}
        </button>
      </div>
    </div>
  );
};

export default CookieBanner;
